/**
 * en-IN formatting — INR + lakh/crore + DD-MMM-YYYY dates.
 *
 * Architecture §8.5: these are wired via Intl from day one; not waiting on i18n.
 */
const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const inrShort = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 1 });
const inrExact = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function paiseToInr(paiseStr: string | bigint): string {
  const paise = typeof paiseStr === "string" ? BigInt(paiseStr || "0") : paiseStr;
  const rupees = Number(paise / 100n);
  return inrFormatter.format(rupees);
}

export function paiseToInrShort(paiseStr: string | bigint): string {
  const paise = typeof paiseStr === "string" ? BigInt(paiseStr || "0") : paiseStr;
  const rupees = Number(paise / 100n);
  const abs = Math.abs(rupees);
  const sign = rupees < 0 ? "-" : "";
  if (abs >= 1_00_00_000) return `${sign}₹${inrShort.format(abs / 1_00_00_000)}Cr`;
  if (abs >= 1_00_000) return `${sign}₹${inrShort.format(abs / 1_00_000)}L`;
  return inrFormatter.format(rupees);
}

/**
 * Spelled-out lakh/crore for hero headlines: `₹14.8 lakh`, `₹1.2 crore`.
 * Below ₹1 lakh it falls back to the full grouped rupee figure. Mirrors
 * {@link paiseToInrShort} but with the word, not the `L`/`Cr` suffix.
 */
export function paiseToInrLakhWord(paiseStr: string | bigint): string {
  const paise = typeof paiseStr === "string" ? BigInt(paiseStr || "0") : paiseStr;
  const rupees = Number(paise / 100n);
  const abs = Math.abs(rupees);
  const sign = rupees < 0 ? "-" : "";
  if (abs >= 1_00_00_000) return `${sign}₹${inrShort.format(abs / 1_00_00_000)} crore`;
  if (abs >= 1_00_000) return `${sign}₹${inrShort.format(abs / 1_00_000)} lakh`;
  return inrFormatter.format(rupees);
}

export function paiseToInrExact(paiseStr: string | bigint): string {
  const paise = typeof paiseStr === "string" ? BigInt(paiseStr || "0") : paiseStr;
  const rupees = Number(paise) / 100;
  return inrExact.format(rupees);
}

/**
 * Convert a user-entered INR amount to integer paise without floating point.
 * Accepts commas and the rupee sign; returns null for malformed input.
 */
export function rupeesToPaise(input: string): bigint | null {
  const cleaned = input.replace(/[₹,\s]/g, "").trim();
  if (cleaned === "" || cleaned === "-") return 0n;
  if (!/^\d*(\.\d{0,2})?$/.test(cleaned)) return null;
  const [rupees = "0", paise = ""] = cleaned.split(".");
  return BigInt(rupees || "0") * 100n + BigInt((paise + "00").slice(0, 2) || "0");
}

export function rupeesToPaiseString(input: string): string | null {
  const paise = rupeesToPaise(input);
  return paise === null ? null : paise.toString();
}

export function paiseToRupeesInput(paiseStr: string | bigint): string {
  const paise = typeof paiseStr === "string" ? BigInt(paiseStr || "0") : paiseStr;
  const rupees = paise / 100n;
  const remainder = paise % 100n;
  return remainder === 0n
    ? rupees.toString()
    : `${rupees.toString()}.${remainder.toString().padStart(2, "0")}`;
}

/** `1250` basis points → `'12.5'`. */
export function bpToPercent(bp: number): string {
  return (bp / 100).toString();
}

/**
 * Money in a style/order's own currency: `₹610.69` for INR, `EUR 11.50`
 * otherwise. (Lifted from the duplicate `money` / `styleMoney` locals in
 * `costing-section.tsx` + `style-overview-page.tsx` — AGENTS.md §7 rule 17.)
 */
export function paiseToMoney(paiseStr: string | bigint, currency: string): string {
  if (currency === "INR") return paiseToInrExact(paiseStr);
  const amount = Number(paiseStr) / 100;
  return `${currency} ${amount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * A `*Milli` quantity (integer ×1000) + its unit → `'1,400.8 m'`.
 * (Lifted from `materials-section.tsx`'s `qty`.)
 */
export function milliToQty(milli: number, uom: string): string {
  const units = milli / 1000;
  const text = Number.isInteger(units)
    ? units.toLocaleString("en-IN")
    : units.toLocaleString("en-IN", { maximumFractionDigits: 1 });
  return `${text} ${uom}`;
}

const dateFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
});

export function formatDate(value: string | Date): string {
  return dateFormatter.format(value instanceof Date ? value : new Date(value));
}

const dateTimeFormatter = new Intl.DateTimeFormat("en-IN", {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
});

/** "10 May 2026, 04:32" — for the timeline secondary line. */
export function formatDateTime(value: string | Date): string {
  return dateTimeFormatter.format(value instanceof Date ? value : new Date(value));
}

/**
 * Compute a 1–2 character initials block from a display name.
 *
 *   "Rakesh"             → "RA"
 *   "Rakesh Sharma"      → "RS"
 *   "Sharma Industries"  → "SI"
 *   ""                   → "?"
 *
 * Used by avatar / logo chip components when no image is set. Lifted
 * here because three call-sites (UserAvatar, LogoUploader's preview,
 * the company route's logo initials) had grown the same helper.
 */
export function deriveInitials(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Format an Indian phone number as `+91 XXXXX XXXXX`. Accepts:
 *   - 10 digits     `8888888888`        → `+91 88888 88888`
 *   - 12 digits     `918888888888`      → `+91 88888 88888`
 *   - +91 prefix    `+918888888888`     → `+91 88888 88888`
 *
 * Anything else is returned unchanged so we never silently mangle a
 * value the schema accepted but we don't recognise.
 */
export function formatPhoneIN(value: string): string {
  const digits = value.replace(/\D/g, "");
  let local: string;
  if (digits.length === 10) local = digits;
  else if (digits.length === 12 && digits.startsWith("91")) local = digits.slice(2);
  else return value;
  return `+91 ${local.slice(0, 5)} ${local.slice(5)}`;
}

/**
 * Loose relative-time formatter for the activity timeline.
 *
 * Matches the precision people actually skim for: "just now", "5 min
 * ago", "2 hours ago", "yesterday", "3 days ago", then falls back to
 * the absolute date. We deliberately don't use `Intl.RelativeTimeFormat`
 * here — its output ("3 days ago") is right but it forces us to choose
 * the unit ourselves anyway, and we want the "yesterday" / "today"
 * special cases that read more naturally.
 *
 * Pass a reference time (`now`) to keep tests deterministic and to
 * align with the frozen-clock pattern in domain code (`shared/clock`).
 */
export function formatRelativeTime(value: string | Date, now: Date = new Date()): string {
  const then = value instanceof Date ? value : new Date(value);
  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.round(diffMs / 1000);
  const diffMin = Math.round(diffSec / 60);
  const diffHour = Math.round(diffMin / 60);
  const diffDay = Math.round(diffHour / 24);

  // Future timestamps (clock skew, server-vs-client drift) — show as "just now".
  if (diffSec < 30) return "just now";
  if (diffMin < 1) return `${diffSec} sec ago`;
  if (diffMin < 60) return `${diffMin} min ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour === 1 ? "" : "s"} ago`;
  if (diffDay === 1) return "yesterday";
  if (diffDay < 7) return `${diffDay} days ago`;
  if (diffDay < 30) {
    const weeks = Math.round(diffDay / 7);
    return `${weeks} week${weeks === 1 ? "" : "s"} ago`;
  }
  // Beyond a month, the absolute date is more useful than "3 months ago".
  return formatDate(then);
}
